import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, User, Mail, Phone, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";

const Auth = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmailState] = useState("");
  const [signupUserId, setSignupUserId] = useState("");
  const [signupUserName, setSignupUserName] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup form state
  const [signupName, setSignupName] = useState("");
  const [signupEmailInput, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");

  // Resend email state
  const [resendingEmail, setResendingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  // Check if email is verified via polling
  const checkEmailVerified = async () => {
    if (!signupUserId) return false;
    
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("email_verified")
        .eq("id", signupUserId)
        .single();
      
      if (error) {
        console.error("Error checking verification:", error);
        return false;
      }
      
      return data?.email_verified === true;
    } catch (error) {
      console.error("Error in checkEmailVerified:", error);
      return false;
    }
  };

  // Start polling for email verification
  useEffect(() => {
    if (signupSuccess && signupUserId) {
      // Start polling every 5 seconds
      pollingRef.current = setInterval(async () => {
        const verified = await checkEmailVerified();
        if (verified) {
          // Stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
          }
          
          toast({
            title: "E-mail confirmado!",
            description: "Sua conta foi ativada. Redirecionando...",
          });
          
          // Redirect to login after a short delay
          setTimeout(() => {
            setSignupSuccess(false);
            navigate("/auth");
          }, 2000);
        }
      }, 5000);
      
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [signupSuccess, signupUserId, navigate, toast]);

  // Helper function to check if user is admin
  const checkIsAdmin = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .single();
    return !!data;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // Check if email is verified in our custom system
        const { data: profile } = await supabase
          .from("profiles")
          .select("email_verified")
          .eq("id", session.user.id)
          .single();
        
        if (profile?.email_verified) {
          // Check if user is admin
          const isAdmin = await checkIsAdmin(session.user.id);
          navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
        }
      }
      setCheckingSession(false);
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // Check if email is verified in our custom system
        const { data: profile } = await supabase
          .from("profiles")
          .select("email_verified")
          .eq("id", session.user.id)
          .single();
        
        if (profile?.email_verified) {
          // Check if user is admin
          const isAdmin = await checkIsAdmin(session.user.id);
          navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
        }
      }
      setCheckingSession(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) throw error;

      if (data.user) {
        // Check if email is verified in our custom system
        const { data: profile } = await supabase
          .from("profiles")
          .select("email_verified")
          .eq("id", data.user.id)
          .single();
        
        if (!profile?.email_verified) {
          // Sign out the user since email is not confirmed
          await supabase.auth.signOut();
          setSignupSuccess(true);
          setSignupEmailState(loginEmail);
          setSignupUserId(data.user.id);
          setSignupUserName(data.user.user_metadata?.full_name || "");
          toast({
            title: "E-mail não confirmado",
            description: "Por favor, confirme seu e-mail antes de acessar a plataforma.",
            variant: "destructive",
          });
          return;
        }

        // Check if user is admin and redirect accordingly
        const isAdmin = await checkIsAdmin(data.user.id);
        
        toast({
          title: t('auth.loginSuccess'),
          description: t('auth.loginSuccessDesc'),
        });
        
        navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
      }
    } catch (error: any) {
      let message = error.message;
      if (error.message === "Invalid login credentials") {
        message = t('auth.invalidCredentials');
      } else if (error.message === "Email not confirmed") {
        message = "Por favor, confirme seu e-mail antes de acessar.";
      }
      toast({
        title: t('auth.loginError'),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSendingReset(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: t('auth.resetEmailSent'),
        description: t('auth.resetEmailSentDesc'),
      });
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error: any) {
      toast({
        title: t('auth.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const sendVerificationEmail = async (userId: string, email: string, name: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("send-verification-email", {
        body: { userId, email, name },
      });

      if (error) throw error;
      
      return { success: true };
    } catch (error: any) {
      console.error("Error sending verification email:", error);
      return { success: false, error: error.message };
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (signupPassword !== signupConfirmPassword) {
      toast({
        title: t('auth.error'),
        description: t('auth.passwordMismatch'),
        variant: "destructive",
      });
      return;
    }

    if (signupPassword.length < 6) {
      toast({
        title: t('auth.error'),
        description: t('auth.passwordTooShort'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signupEmailInput,
        password: signupPassword,
        options: {
          data: {
            full_name: signupName,
            phone: signupPhone,
          },
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          throw new Error(t('auth.emailAlreadyRegistered'));
        }
        throw error;
      }

      // Check if user was created
      if (data.user) {
        // Send custom verification email
        const emailResult = await sendVerificationEmail(
          data.user.id,
          signupEmailInput,
          signupName
        );

        if (!emailResult.success) {
          console.error("Failed to send verification email:", emailResult.error);
          // Continue anyway, user can resend
        }

        setSignupSuccess(true);
        setSignupEmailState(signupEmailInput);
        setSignupUserId(data.user.id);
        setSignupUserName(signupName);
        
        // Clear form
        setSignupName("");
        setSignupEmail("");
        setSignupPhone("");
        setSignupPassword("");
        setSignupConfirmPassword("");
      }
    } catch (error: any) {
      toast({
        title: t('auth.signupError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!signupUserId || !signupEmail) {
      toast({
        title: "Erro",
        description: "Informações de usuário não encontradas. Por favor, tente novamente.",
        variant: "destructive",
      });
      return;
    }

    setResendingEmail(true);
    try {
      const result = await sendVerificationEmail(signupUserId, signupEmail, signupUserName);

      if (!result.success) {
        throw new Error(result.error || "Falha ao enviar e-mail");
      }

      toast({
        title: "E-mail reenviado!",
        description: "Verifique sua caixa de entrada e spam.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao reenviar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || newEmail === signupEmail) {
      toast({
        title: "E-mail inválido",
        description: "Por favor, insira um e-mail diferente.",
        variant: "destructive",
      });
      return;
    }

    setResendingEmail(true);
    try {
      toast({
        title: "E-mail atualizado",
        description: "Faça um novo cadastro com o e-mail correto.",
      });
      setEditingEmail(false);
      setSignupSuccess(false);
      setSignupEmail(newEmail);
      setNewEmail("");
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResendingEmail(false);
    }
  };

  if (checkingSession) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Show email confirmation pending screen
  if (signupSuccess) {
    return (
      <Layout>
        <SEO 
          title="Confirme seu E-mail - TUPJA"
          description="Confirme seu e-mail para acessar a plataforma"
        />
        <div className="min-h-screen flex items-start justify-center bg-gradient-to-b from-background to-muted/30 pt-[25px] pb-20 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl font-display text-primary">
                Confirme seu E-mail
              </CardTitle>
              <CardDescription className="text-base">
                Enviamos um link de confirmação para:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <p className="font-medium text-lg">{signupEmail}</p>
              </div>

              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Clique no link enviado para seu e-mail para ativar sua conta</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>Verifique também sua pasta de spam ou lixo eletrônico</span>
                </div>
                <div className="flex items-start gap-2">
                  <Loader2 className="w-4 h-4 text-primary animate-spin mt-0.5 shrink-0" />
                  <span>Esta página será atualizada automaticamente quando você confirmar</span>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <p className="text-sm text-center text-muted-foreground">
                  Não recebeu o e-mail?
                </p>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleResendEmail}
                  disabled={resendingEmail}
                >
                  {resendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Reenviando...
                    </>
                  ) : (
                    "Reenviar E-mail de Confirmação"
                  )}
                </Button>

                {!editingEmail ? (
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm"
                    onClick={() => setEditingEmail(true)}
                  >
                    Errou o e-mail? Clique aqui para corrigir
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="new-email">Novo E-mail</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Digite o e-mail correto"
                    />
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setEditingEmail(false);
                          setNewEmail("");
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={handleUpdateEmail}
                        disabled={resendingEmail}
                      >
                        Confirmar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <Button 
                  variant="link" 
                  className="w-full"
                  onClick={() => setSignupSuccess(false)}
                >
                  Voltar para Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title={t('auth.seoTitle')}
        description={t('auth.seoDescription')}
      />
      <div className="min-h-screen flex items-start justify-center bg-gradient-to-b from-background to-muted/30 pt-[25px] pb-20 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-display text-primary">
              {t('auth.title')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('auth.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('auth.password')}
                    </Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {t('auth.loggingIn')}
                      </>
                    ) : (
                      t('auth.login')
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                    <DialogTrigger asChild>
                      <Button variant="link" className="text-sm text-muted-foreground">
                        {t('auth.forgotPassword')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('auth.forgotPasswordTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('auth.forgotPasswordDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                          <Input
                            id="forgot-email"
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            required
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={isSendingReset}>
                          {isSendingReset ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              {t('auth.sending')}
                            </>
                          ) : (
                            t('auth.sendResetLink')
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {t('auth.fullName')}
                    </Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      placeholder={t('auth.fullNamePlaceholder')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signupEmailInput}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {t('auth.phone')}
                    </Label>
                    <Input
                      id="signup-phone"
                      type="tel"
                      value={signupPhone}
                      onChange={(e) => setSignupPhone(e.target.value)}
                      placeholder={t('auth.phonePlaceholder')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('auth.password')}
                    </Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm-password" className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('auth.confirmPassword')}
                    </Label>
                    <Input
                      id="signup-confirm-password"
                      type="password"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {t('auth.signingUp')}
                      </>
                    ) : (
                      t('auth.signup')
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Auth;
